import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IonicModule, ModalController } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { CgvSelectors, AcceptedNewGcs, LoadCgvList, UserProfileSelector } from '@smatchy/core';
import { CgvVersionEntity } from '@smatchy/sdk';
import { filter, firstValueFrom } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { LanguageService } from '../../../../../core/services/language.service';
import { captureException } from '../../../../../tracing';

/** The only dismissal home.component accepts for this modal. */
export const GCS_ACCEPTED_ROLE = 'gcs-accepted';
@Component({
  selector: 'smatchy-term-gcs-policy',
  templateUrl: './term-gcs-policy.component.html',
  styleUrls: ['./term-gcs-policy.component.scss'],
  imports: [IonicModule, TranslateModule, CommonModule, FormsModule],
})
export class TermGcsPolicyModalComponent implements OnInit {
  accepted = false;
  userId: string | null = null;
  loading = false;
  submitFailed = false;
  policyHtml!: SafeHtml;
  /** Shown in a chip next to the title so the member knows which text they accept. */
  version: string | null = null;

  constructor(
    private sanitizer: DomSanitizer,
    private readonly _store: Store,
    private readonly _modalCtrl: ModalController,
    private readonly _languageService: LanguageService,
  ) {}

  async ngOnInit() {
    const profile = await firstValueFrom(this._store.select(UserProfileSelector.profile));
    // acceptedGcs is not on UserProfilePopulatedEntity. auth.state.ts already
    // reads it this way, which says what is being asked for instead of giving
    // up checking on the whole expression.
    this.accepted = (profile as { acceptedGcs?: boolean } | undefined)?.acceptedGcs === true;
    this.userId = profile?.userId ?? null;

    this._store.dispatch(
      new LoadCgvList({
        page: 1,
        limit: 1,
        type: 'gcs',
        sortBy: 'updatedAt',
        sortOrder: 'desc',
      }),
    );

    this._store
      .select(CgvSelectors.lastVersion)
      .pipe(filter((rs) => !!rs))
      .subscribe((rs: CgvVersionEntity) => {
        const currentLang = this._languageService.getCurrentLang();
        const content =
          currentLang === 'en'
            ? rs.contentEn || rs.content || rs.contentFr
            : rs.contentFr || rs.content || rs.contentEn;
        this.policyHtml = this.sanitizer.bypassSecurityTrustHtml(content ?? '');
        this.version = rs.version ?? null;
      });
  }

  /**
   * Dismiss with the role the modal's canDismiss guard requires.
   *
   * Without a role, the Android hardware back button dismisses this overlay
   * even though backdropDismiss is false, and someone leaves without the
   * acceptance ever being recorded. home.component only lets the dismissal
   * through when it carries this role, which only this method sets, and only
   * after the API has answered.
   */
  close() {
    this._modalCtrl.dismiss(undefined, GCS_ACCEPTED_ROLE);
  }

  changAccepted(value: boolean) {
    this.accepted = value;
  }

  async onSubmit() {
    if (!this.userId || !this.accepted) {
      return;
    }
    this.loading = true;
    this.submitFailed = false;
    try {
      // firstValueFrom, not a bare await. store.dispatch returns an Observable:
      // awaiting it resolved at once and never rejected, so the modal closed
      // whether the API had recorded the acceptance or not, and the catch below
      // could never run. The person pressed Accept, the modal disappeared, and
      // the acceptance may never have existed.
      await firstValueFrom(this._store.dispatch(new AcceptedNewGcs(this.userId)));
      this.close();
    } catch (error) {
      captureException(error);
      this.submitFailed = true;
    } finally {
      this.loading = false;
    }
  }
}
